<template>
	<div
		class="g-block-pay-file-list"
		:class="{
			'g-block-pay-file-list--edit': editable,
			'is-active': editable && active,
			'g-block-pay-file-list--h5': h5,
			'g-block-pay-file-list--compact': compact,
			'g-block-pay-file-list--unlocked': isUnlocked,
		}"
		:style="{ '--pay-file-list-radius': `${fileData.borderRadius}px` }"
		@click="onWrapClick"
	>
		<div class="g-block-pay-file-list__card" :style="cardStyle">
			<div class="g-block-pay-file-list__header">
				<div class="g-block-pay-file-list__file-icon" aria-hidden="true">
					<img
						class="g-block-pay-file-list__file-icon-img"
						:src="fileIconUrl"
						:alt="fileIconMark"
					/>
				</div>
				<div class="g-block-pay-file-list__file-info">
					<div class="g-block-pay-file-list__file-name">{{ displayFileName }}</div>
					<div v-if="metaText" class="g-block-pay-file-list__file-meta">{{ metaText }}</div>
					<div class="g-block-pay-file-list__header-features">
						<template v-for="(feature, index) in fileData.headerFeatures" :key="feature.id">
							<span
								class="g-block-pay-file-list__header-feature"
								:class="`is-${feature.icon}`"
							>
								<i :class="featureIconName(feature.icon)"></i>
								<span>{{ feature.text }}</span>
							</span>
							<span
								v-if="index < fileData.headerFeatures.length - 1"
								class="g-block-pay-file-list__header-feature-divider"
								aria-hidden="true"
							></span>
						</template>
					</div>
				</div>
			</div>

			<div v-if="previewRows.length" class="g-block-pay-file-list__preview-box">
				<div v-if="!isUnlocked" class="g-block-pay-file-list__preview-head">
					<span class="g-block-pay-file-list__preview-badge">可免费试看</span>
					<span class="g-block-pay-file-list__preview-range">前 {{ previewFreeCount }} 页内容</span>
				</div>
				<div class="g-block-pay-file-list__preview-list">
					<div
						v-for="row in previewRows"
						:key="row.key"
						class="g-block-pay-file-list__preview-item"
					>
						<div
							class="g-block-pay-file-list__preview-body"
							:class="{
								'is-locked': row.locked,
								'is-previewable': isRowPreviewable(row),
							}"
							@click="onPreviewClick(row, $event)"
						>
							<span
								class="g-block-pay-file-list__preview-index"
								:class="{ 'is-locked': row.locked }"
							>{{ formatPageIndex(row.index) }}</span>
							<template v-if="!row.locked">
								<img
									v-if="getPreviewImageUrl(row.image)"
									class="g-block-pay-file-list__preview-image"
									:src="getPreviewImageUrl(row.image)"
									alt=""
								/>
								<div v-else class="g-block-pay-file-list__preview-placeholder">暂无预览</div>
							</template>
							<template v-else>
								<img
									v-if="getPreviewImageUrl(row.image)"
									class="g-block-pay-file-list__preview-bg"
									:src="getPreviewImageUrl(row.image)"
									alt=""
								/>
								<div class="g-block-pay-file-list__locked-mask">
								<i class="mbm-iconfont mbm-suoding g-block-pay-file-list__locked-icon" aria-hidden="true"></i>
								</div>
							</template>
						</div>
					</div>
				</div>
				<p v-if="showPreviewDesc" class="g-block-pay-file-list__preview-desc">
					可免费试看前
					<strong>{{ fileData.freePreviewCount }}</strong>
					页，解锁后可查看全部
					<strong>{{ pageCount }}</strong>
					页内容
				</p>
			</div>

			<div v-if="!isUnlocked && !shouldHidePrice" class="g-block-pay-file-list__pay">
				<div class="g-block-pay-file-list__price">
					<span class="g-block-pay-file-list__currency">{{ fileData.currency }}</span>
					<span class="g-block-pay-file-list__price-value">{{ fileData.price }}</span>
				</div>
				<button
					type="button"
					class="g-block-pay-file-list__unlock-btn"
					:style="buttonStyle"
					@click.stop="onUnlockClick"
				>
					<i class="mbm-iconfont mbm-suoding g-block-pay-file-list__unlock-icon"></i>
					<span>{{ fileData.buttonText }}</span>
				</button>
				<div class="g-block-pay-file-list__trust">
					<i class="mbm-iconfont mbm-check g-block-pay-file-list__trust-icon"></i>
					<span>{{ fileData.trustText }}</span>
				</div>
			</div>
		</div>

		<ImageGalleryLightbox
			v-model:visible="lightboxVisible"
			:urls="lightboxUrls"
			:initial-index="lightboxIndex"
		/>
	</div>
</template>

<script>
	import { LOCAL_ASSET } from '../../../utils/localAssets.js';
	import { geditPayUnlockMixin } from '../../../mixins/geditPayUnlock.js';
	import ImageGalleryLightbox from '../../../components/ImageGalleryLightbox.vue';
	import {
		getPayFileListMetaText,
		getPayFileListPreviewRows,
		getPayFileUnlockFileIconKey,
		getPayFileUnlockFileIconMark,
		getPayFileUnlockGalleryUrls,
		getPayFileUnlockPreviewImageUrl,
		isPayFileListUnlocked,
		normalizePayFileListData,
		setPayFileListUnlocked,
	} from '../../../utils/payFileList.js';

	export default {
		name: 'GPayFileListShell',
		mixins: [geditPayUnlockMixin],
		components: { ImageGalleryLightbox },
		props: {
			data: {
				type: Object,
				required: true,
			},
			blockId: {
				type: String,
				default: '',
			},
			editable: {
				type: Boolean,
				default: false,
			},
			active: {
				type: Boolean,
				default: false,
			},
			h5: {
				type: Boolean,
				default: false,
			},
			compact: {
				type: Boolean,
				default: false,
			},
		},
		emits: ['select'],
		data() {
			return {
				unlockedLocal: false,
				lightboxVisible: false,
				lightboxIndex: 0,
			};
		},
		computed: {
			fileData() {
				return normalizePayFileListData(this.data);
			},
			cardStyle() {
				return {
					borderRadius: `${this.fileData.borderRadius}px`,
				};
			},
			buttonStyle() {
				return {
					background: this.fileData.buttonBg,
				};
			},
			displayFileName() {
				return this.fileData.fileName || '未上传文档';
			},
			metaText() {
				return getPayFileListMetaText(this.fileData);
			},
			fileIconMark() {
				return getPayFileUnlockFileIconMark(this.fileData.fileName);
			},
			fileIconUrl() {
				const key = getPayFileUnlockFileIconKey(this.fileData.fileName);
				return LOCAL_ASSET.payFileIcons[key] || LOCAL_ASSET.payFileIcons.file;
			},
			pageCount() {
				return this.fileData.items.length;
			},
			isUnlocked() {
				if (this.editable) return false;
				return this.unlockedLocal || isPayFileListUnlocked(this.blockId) || this.isGeditModulePaid;
			},
			previewFreeCount() {
				if (this.compact) return 1;
				return this.fileData.freePreviewCount;
			},
			previewRows() {
				return getPayFileListPreviewRows(this.fileData, {
					unlocked: this.isUnlocked,
					compact: this.compact,
				});
			},
			showPreviewDesc() {
				return !this.isUnlocked
					&& this.pageCount > 0
					&& this.fileData.freePreviewCount > 0
					&& this.fileData.freePreviewCount < this.pageCount;
			},
			lightboxUrls() {
				const urls = getPayFileUnlockGalleryUrls(this.fileData);
				if (this.isUnlocked) {
					return urls;
				}
				return urls.slice(0, this.fileData.freePreviewCount);
			},
		},
		watch: {
			blockId: {
				immediate: true,
				handler() {
					this.syncUnlockState();
				},
			},
		},
		methods: {
			syncUnlockState() {
				this.unlockedLocal = isPayFileListUnlocked(this.blockId);
			},
			getPreviewImageUrl(image) {
				return getPayFileUnlockPreviewImageUrl(image);
			},
			formatPageIndex(index) {
				return String(index + 1);
			},
			featureIconName(icon) {
				const map = {
					shield: 'mbm-iconfont mbm-anquan',
					download: 'mbm-iconfont mbm-xiazai1',
					valid: 'mbm-iconfont mbm-check',
				};
				return map[icon] || map.shield;
			},
		onWrapClick() {
			if (this.editable) {
				this.$emit('select');
			}
		},
			onUnlockClick() {
				this.runGeditUnlockFlow({
					applyUnlock: () => {
						setPayFileListUnlocked(this.blockId, true);
						this.unlockedLocal = true;
					},
				});
			},
			isRowPreviewable(row) {
				if (!this.h5 || this.editable) return false;
				return Boolean(this.getPreviewImageUrl(row.image));
			},
			onPreviewClick(row, event) {
				if(row.locked){
					this.onUnlockClick();
					return
				}
				if (!this.isRowPreviewable(row)) return;
				event.stopPropagation();
				this.lightboxIndex = row.index;
				this.lightboxVisible = true;
			},
		},
	};
</script>

<style scoped lang="less">
	.g-block-pay-file-list {
		box-sizing: border-box;

		&--edit {
			border: 1px solid transparent;
			border-radius: 4px;
			transition: border-color 0.2s, box-shadow 0.2s;
			cursor: pointer;

			&.is-active {
				border-color: #285af9;
				box-shadow: 0 0 0 1px rgba(40, 90, 249, 0.15);
			}
		}
	}

	.g-block-pay-file-list__card {
		background: #fff;
		overflow: hidden;
		box-shadow: 0 0 12px rgba(0, 0, 0, 0.1);
	}

	.g-block-pay-file-list__header {
		display: flex;
		gap: 14px;
		padding: 18px 10px 14px;
	}

	.g-block-pay-file-list__file-icon {
		flex-shrink: 0;
		width: 56px;
		height: 56px;
		border-radius: 10px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.g-block-pay-file-list__file-icon-img {
		width: 56px;
		height: 56px;
		object-fit: contain;
	}

	.g-block-pay-file-list__file-icon-mark {
		display: none;
	}

	.g-block-pay-file-list__file-info {
		flex: 1;
		min-width: 0;
	}

	.g-block-pay-file-list__file-name {
		font-size: 18px;
		font-weight: 700;
		color: #1a1a1a;
		line-height: 1.35;
		word-break: break-all;
	}

	.g-block-pay-file-list__file-meta {
		margin-top: 6px;
		font-size: 13px;
		color: #999;
		line-height: 1.4;
	}

	.g-block-pay-file-list__header-features {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		margin-top: 8px;
	}

	.g-block-pay-file-list__header-feature {
		display: inline-flex;
		align-items: center;
		gap: 3px;
		font-size: 10px;
		color: #666;
		line-height: 1.3;

		&.is-shield i {
			font-size: 12px;
			color: #52c41a;
		}

		&.is-download i {
			font-size: 10px;
			color: #285af9;
		}

		&.is-valid i {
			font-size: 10px;
			color: #faad14;
		}
	}

	.g-block-pay-file-list__header-feature-divider {
		width: 1px;
		height: 10px;
		margin: 0 8px;
		background: #e8e8e8;
		flex-shrink: 0;
	}

	.g-block-pay-file-list__preview-box {
		margin: 0 10px 10px;
	}

	.g-block-pay-file-list__preview-head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 10px;
	}

	.g-block-pay-file-list__preview-badge {
		padding: 2px 8px;
		border-radius: 4px;
		background: #285af9;
		color: #fff;
		font-size: 11px;
		line-height: 1.4;
	}

	.g-block-pay-file-list__preview-range {
		font-size: 11px;
		color: #999;
		line-height: 1.4;
	}

	.g-block-pay-file-list__preview-list {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 8px;
	}

	.g-block-pay-file-list__preview-item {
		min-height: 0;
		overflow: hidden;
		background: #fff;
		box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);
		border-radius: var(--pay-file-list-radius, 8px);
	}

	.g-block-pay-file-list__preview-body {
		position: relative;
		width: 100%;
		aspect-ratio: 3 / 4;
		overflow: hidden;
		box-sizing: border-box;

		&.is-previewable {
			cursor: pointer;
		}
	}

	.g-block-pay-file-list--edit,
	.g-block-pay-file-list--compact {
		.g-block-pay-file-list__preview-list {
			display: flex;
			flex-direction: column;
			gap: 8px;
		}

		.g-block-pay-file-list__preview-body {
			aspect-ratio: 16 / 10;
		}
	}

	.g-block-pay-file-list--h5 {
		.g-block-pay-file-list__preview-list {
			display: flex;
			flex-direction: column;
		}

		.g-block-pay-file-list__preview-body {
			aspect-ratio: unset;
			height: auto;
		}

		.g-block-pay-file-list__preview-image {
			height: auto;
			object-fit: contain;
		}

		.g-block-pay-file-list__preview-body.is-locked {
			.g-block-pay-file-list__preview-bg {
				position: relative;
				inset: auto;
				width: 100%;
				height: auto;
				object-fit: contain;
			}
		}

		.g-block-pay-file-list__preview-placeholder {
			min-height: 2.4rem;
			height: auto;
		}
	}

	.g-block-pay-file-list--compact {
		.g-block-pay-file-list__preview-box {
			margin: 0 10px 10px;
		}

		.g-block-pay-file-list__preview-head {
			margin-bottom: 6px;
		}

		.g-block-pay-file-list__preview-list {
			gap: 6px;
		}

		.g-block-pay-file-list__preview-index {
			top: 4px;
			left: 4px;
			min-width: 16px;
			height: 14px;
			font-size: 9px;
		}

		.g-block-pay-file-list__locked-icon {
			font-size: 20px;
		}

		.g-block-pay-file-list__locked-title {
			font-size: 9px;
		}

		.g-block-pay-file-list__locked-range {
			font-size: 8px;
		}

		.g-block-pay-file-list__pay {
			padding: 10px 10px;
		}

		.g-block-pay-file-list__currency {
			font-size: 12px;
		}

		.g-block-pay-file-list__price-value {
			font-size: 22px;
		}

		.g-block-pay-file-list__unlock-btn {
			margin-top: 8px;
			padding: 12px 12px;
			font-size: 15px;
		}

		.g-block-pay-file-list__trust {
			margin-top: 6px;
			font-size: 10px;
		}
	}

	.g-block-pay-file-list__preview-index {
		position: absolute;
		top: 6px;
		left: 6px;
		z-index: 2;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-width: 20px;
		height: 18px;
		padding: 0 5px;
		border-radius: 4px;
		background: #285af9;
		color: #fff;
		font-size: 11px;
		font-weight: 600;
		line-height: 1;
		box-sizing: border-box;

		&.is-locked {
			background: rgba(0, 0, 0, 0.28);
		}
	}

	.g-block-pay-file-list__preview-image,
	.g-block-pay-file-list__preview-bg {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.g-block-pay-file-list__preview-bg {
		position: absolute;
		inset: 0;
		filter: blur(10px);
		transform: scale(1.06);
	}

	.g-block-pay-file-list__preview-placeholder {
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		background: #f0f2f5;
		font-size: 10px;
		color: #bbb;
	}

	.g-block-pay-file-list__locked-mask {
		position: absolute;
		inset: 0;
		z-index: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 4px;
		background: rgba(0, 0, 0, 0.35);
	}

	.g-block-pay-file-list__locked-icon {
		font-size: 22px;
		color: #fff;
	}

	.g-block-pay-file-list__locked-title {
		font-size: 11px;
		color: #fff;
		line-height: 1.3;
	}

	.g-block-pay-file-list__locked-range {
		font-size: 10px;
		color: rgba(255, 255, 255, 0.8);
		line-height: 1.3;
	}

	.g-block-pay-file-list__preview-desc {
		margin: 12px 0 0;
		font-size: 12px;
		color: #999;
		text-align: center;
		line-height: 1.5;

		strong {
			color: #285af9;
			font-weight: 600;
		}
	}

	.g-block-pay-file-list__pay {
		padding: 16px 16px;
	}

	.g-block-pay-file-list__price {
		display: flex;
		align-items: baseline;
		justify-content: center;
		color: #ff4d4f;
		line-height: 1;
	}

	.g-block-pay-file-list__currency {
		font-size: 16px;
		font-weight: 600;
		margin-right: 2px;
	}

	.g-block-pay-file-list__price-value {
		font-size: 32px;
		font-weight: 700;
	}

	.g-block-pay-file-list__unlock-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		width: 100%;
		margin-top: 12px;
		border: none;
		border-radius: 999px;
		padding: 12px 16px;
		font-size: 15px;
		font-weight: 600;
		color: #fff;
		cursor: pointer;
		line-height: 1.2;

		&:active {
			opacity: 0.92;
		}
	}

	.g-block-pay-file-list__unlock-icon {
		font-size: 17px;
		flex-shrink: 0;
	}

	.g-block-pay-file-list__trust {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 4px;
		margin-top: 10px;
		font-size: 12px;
		color: #52c41a;
		line-height: 1.4;
	}

	.g-block-pay-file-list__trust-icon {
		font-size: 12px;
	}

	.g-block-pay-file-list--h5 {
		.g-block-pay-file-list__header {
			gap: 0.28rem;
			padding: 0.36rem 0.2rem 0.28rem;
		}

		.g-block-pay-file-list__file-icon {
			width: 1.24rem;
			height: 1.24rem;
			border-radius: 0.2rem;
		}

		.g-block-pay-file-list__file-icon-img {
			width: 1.12rem;
			height: 1.12rem;
		}

		.g-block-pay-file-list__file-icon-mark {
			display: none;
		}

		.g-block-pay-file-list__file-name {
			font-size: 0.36rem;
		}

		.g-block-pay-file-list__file-meta {
			margin-top: 0.12rem;
			font-size: 0.26rem;
		}

		.g-block-pay-file-list__header-features {
			margin-top: 0.16rem;
		}

		.g-block-pay-file-list__header-feature {
			gap: 0.06rem;
			font-size: 0.2rem;

			&.is-shield i {
				font-size: 0.24rem;
			}

			&.is-download i,
			&.is-valid i {
				font-size: 0.2rem;
			}
		}

		.g-block-pay-file-list__header-feature-divider {
			height: 0.2rem;
			margin: 0 0.16rem;
		}

		.g-block-pay-file-list__preview-box {
			margin: 0 0.2rem 0.2rem;
		}

		.g-block-pay-file-list__preview-head {
			margin-bottom: 0.2rem;
		}

		.g-block-pay-file-list__preview-badge {
			padding: 0.04rem 0.16rem;
			font-size: 0.22rem;
		}

		.g-block-pay-file-list__preview-range {
			font-size: 0.22rem;
		}

		.g-block-pay-file-list__preview-list {
			gap: 0.16rem;
		}

		.g-block-pay-file-list__preview-item {
			border-radius: 0.16rem;
		}

		.g-block-pay-file-list__preview-index {
			top: 0.12rem;
			left: 0.12rem;
			min-width: 0.4rem;
			height: 0.36rem;
			padding: 0 0.1rem;
			border-radius: 0.08rem;
			font-size: 0.22rem;
		}

		.g-block-pay-file-list__preview-placeholder {
			font-size: 0.2rem;
		}

		.g-block-pay-file-list__locked-mask {
			gap: 0.08rem;
		}

		.g-block-pay-file-list__locked-icon {
			font-size: 0.4rem;
		}

		.g-block-pay-file-list__locked-title {
			font-size: 0.22rem;
		}

		.g-block-pay-file-list__locked-range {
			font-size: 0.2rem;
		}

		.g-block-pay-file-list__preview-desc {
			margin-top: 0.24rem;
			font-size: 0.24rem;
		}

		.g-block-pay-file-list__pay {
			padding: 0.32rem;
		}

		.g-block-pay-file-list__currency {
			font-size: 0.32rem;
		}

		.g-block-pay-file-list__price-value {
			font-size: 0.64rem;
		}

		.g-block-pay-file-list__unlock-btn {
			gap: 0.12rem;
			margin-top: 0.24rem;
			padding: 0.24rem 0.32rem;
			font-size: 0.3rem;
		}

		.g-block-pay-file-list__unlock-icon {
			font-size: 0.34rem;
		}

		.g-block-pay-file-list__trust {
			gap: 0.08rem;
			margin-top: 0.2rem;
			font-size: 0.24rem;
		}
	}
</style>
